import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Label, Select } from "@components";
import { RootProductId, BudgetCategory, Category } from "@models";
import FlatCategorySelection from "./FlatCategorySelection";
import { ConfigModuleSetting } from "./types";
import { UpdatedDivision } from "@models/Division";
import { OneOf } from "@util/types";
import { useStaticData } from "@hooks";
import GroupedCategorySelection from "./GroupedCategorySelection";

type Props = {
	productId: RootProductId;
	budgetCategory: BudgetCategory;
	enabledCategories?: string[];
};

export default function CategoriesSelection(props: Props) {
	const { productId, budgetCategory, enabledCategories } = props;
	const { control, watch, formState } =
		useFormContext<OneOf<ConfigModuleSetting, UpdatedDivision>>();

	const product = useStaticData().getProductOfId(productId);

	if (productId === "spend") {
		const selectedValue = watch("categories.0");
		const originalCategories = formState.defaultValues?.categories || [];

		const shouldShowDeprecatedCategory = (category: Category) => {
			if (!category.isDeprecated) return true;

			// Show if currently selected
			if (category.id === selectedValue) return true;

			// Show if it was originally selected but now deselected (not yet saved)
			return (
				originalCategories.includes(category.id) &&
				category.id !== selectedValue
			);
		};

		return (
			<>
				<Label htmlFor={`${productId}-category-select`}>Kategorie</Label>
				<Controller
					name={"categories.0"}
					control={control}
					render={({ field }) => (
						<Select
							id={`${productId}-category-select`}
							{...field}
							value={selectedValue}
						>
							{budgetCategory.subCategories.map(
								(category) =>
									shouldShowDeprecatedCategory(category) && (
										<option key={category.id} value={category.id}>
											{category.name}
										</option>
									)
							)}
						</Select>
					)}
				/>
			</>
		);
	}

	if (
		productId === "mobility" ||
		productId === "fitAndRelax" ||
		productId === "homeAndWork"
	) {
		return (
			<Controller
				name="categories"
				control={control}
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				render={({ field: { ref, ...fieldRest } }) =>
					product.categoryGroups?.length ? (
						<GroupedCategorySelection
							{...fieldRest}
							budgetCategory={budgetCategory}
							enabledCategories={enabledCategories}
						/>
					) : (
						<FlatCategorySelection
							{...fieldRest}
							budgetCategory={budgetCategory}
						/>
					)
				}
			/>
		);
	}

	return <></>;
}
